import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import Layout from "@/components/Layout";
import { useSEO } from "@/hooks/useSEO";
import { ArrowLeft, ArrowRight, Layers, Pen, FileText, Sparkles } from "lucide-react";

const C = {
  bg: "#070707", bg2: "#11100E", card: "#181614", text: "#F5F2EB", text2: "#B5B0A7",
  gold: "#C9A055", red: "#FF3B30", bSub: "rgba(245,242,235,0.10)", bGold: "rgba(201,160,85,0.35)",
};
const F = { brush: "'Permanent Marker', cursive", cond: "'Saira Condensed', sans-serif", hand: "'Caveat', cursive", sans: "'DM Sans', 'Outfit', sans-serif" };

function useVisible(threshold = 0.15) {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setV(true); }, { threshold });
    obs.observe(ref.current);
    const timer = setTimeout(() => setV(true), 600);
    return () => { obs.disconnect(); clearTimeout(timer); };
  }, [threshold]);
  return [ref, v];
}

const MarkerStroke = ({ color = C.gold, width = "100%", height = "6px", className = "" }) => (
  <div className={className} style={{ width, height, background: color, borderRadius: "3px", transform: "rotate(-0.8deg)", opacity: 0.85, boxShadow: `0 0 12px ${color}40` }} />
);

const STEP_ICONS = [FileText, Pen, Layers, Sparkles];

export default function MCProcess() {
  useSEO({ title: "The Process — Marker Collection", description: "See how every piece is made. From blank page to finished art, mark by mark." });
  const navigate = useNavigate();
  const [pageInfo, setPageInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [heroRef, heroVis] = useVisible(0.1);
  const [stepsRef, stepsVis] = useVisible(0.08);
  const [toolsRef, toolsVis] = useVisible(0.1);

  const [show, setShow] = useState(false);
  useEffect(() => { if (!loading) { const t = setTimeout(() => setShow(true), 100); return () => clearTimeout(t); } }, [loading]);

  useEffect(() => {
    http.get("/marker-collection/public/pages").then(({ data }) => {
      setPageInfo(data?.process || {});
      if (data?.process?.enabled === false) navigate("/marker-collection");
    }).catch(() => {}).finally(() => setLoading(false));
  }, [navigate]);

  const stepPairs = (pageInfo?.steps || "").split("|");
  const steps = [];
  for (let i = 0; i < stepPairs.length - 1; i += 2) {
    steps.push({ title: stepPairs[i], desc: stepPairs[i + 1] });
  }

  const toolPairs = (pageInfo?.tools || "").split("|");
  const tools = [];
  for (let i = 0; i < toolPairs.length - 1; i += 2) {
    tools.push({ name: toolPairs[i], desc: toolPairs[i + 1] });
  }

  if (loading) return (
    <Layout><div className="min-h-screen flex items-center justify-center" style={{ background: C.bg }}>
      <div className="w-16 h-16 border-2 animate-spin" style={{ borderColor: `${C.gold}30`, borderTopColor: C.gold }} />
    </div></Layout>
  );

  return (
    <Layout>
      <div style={{ background: C.bg, color: C.text, minHeight: "100vh" }}>
        <style>{`
          .mc-grit::before { content:''; position:absolute; inset:0; width:100%; height:100%; background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E"); pointer-events:none; z-index:0; }
          @keyframes mc-draw-line { from { stroke-dashoffset: 1000; } to { stroke-dashoffset: 0; } }
        `}</style>

        {/* Hero */}
        <section ref={heroRef} className="relative mc-grit overflow-hidden" style={{ background: C.bg, borderBottom: `1px solid ${C.bSub}` }} data-testid="mc-process-hero">
          {/* Animated drawing line */}
          <svg className="absolute top-16 right-10 w-[350px] h-[250px] pointer-events-none opacity-[0.06]" viewBox="0 0 350 250">
            <path d="M10 120 Q90 20 175 120 T340 120" fill="none" stroke={C.gold} strokeWidth="3"
              strokeDasharray="1000" style={{ animation: heroVis ? "mc-draw-line 3s ease forwards" : "none" }} />
          </svg>
          <div className="max-w-7xl mx-auto px-6 py-14 md:py-20 relative z-10">
            <Link to="/marker-collection" className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase font-bold mb-6 hover:opacity-70 transition-opacity"
              style={{ fontFamily: F.sans, color: C.gold }} data-testid="mc-process-back">
              <ArrowLeft size={14} /> Back to Home
            </Link>
            <div className={`transition-all duration-700 ${show || heroVis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <span className="inline-block text-[10px] tracking-[0.4em] uppercase font-bold px-4 py-1.5 mb-4"
                style={{ fontFamily: F.sans, background: C.red, color: "#fff" }}>BEHIND THE SCENES</span>
              <h1 className="text-5xl sm:text-6xl lg:text-[6rem] font-black leading-[0.85] tracking-tight mb-4" style={{ fontFamily: F.brush, color: C.text }} data-testid="mc-process-title">
                {pageInfo?.heading || "MARK\nBY MARK."}
              </h1>
              <MarkerStroke width="80px" className="mb-5" />
              <p className="text-base max-w-xl leading-relaxed" style={{ fontFamily: F.sans, color: C.text2 }}>
                {pageInfo?.text || "Every piece starts the same way: a blank page and a marker. No digital tools, no projectors, no shortcuts."}
              </p>
            </div>
          </div>
        </section>

        {/* Steps */}
        {steps.length > 0 && (
          <section ref={stepsRef} className="relative mc-grit" style={{ background: C.bg2, borderBottom: `1px solid ${C.bSub}` }} data-testid="mc-process-steps">
            <div className="max-w-7xl mx-auto px-6 py-14 md:py-20 relative z-10">
              <h2 className={`text-3xl sm:text-4xl font-black tracking-tight mb-12 text-center transition-all duration-700 ${show || stepsVis ? "opacity-100" : "opacity-0"}`}
                style={{ fontFamily: F.cond, color: C.text }}>THE JOURNEY OF EVERY PIECE</h2>

              <div className="relative">
                {/* Vertical connector line — desktop only */}
                <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px" style={{ background: `${C.gold}20` }} />

                <div className="space-y-8 lg:space-y-0">
                  {steps.map((step, i) => {
                    const Icon = STEP_ICONS[i] || Pen;
                    const isLeft = i % 2 === 0;
                    return (
                      <div key={i} className={`lg:grid lg:grid-cols-2 lg:gap-16 transition-all duration-600 ${show || stepsVis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                        style={{ transitionDelay: `${200 + i * 150}ms` }} data-testid={`mc-process-step-${i}`}>
                        {/* Content */}
                        <div className={`p-6 ${isLeft ? "lg:text-right" : "lg:col-start-2"}`} style={{ background: `${C.gold}05`, border: `1px solid ${C.bGold}` }}>
                          <div className={`flex items-center gap-3 mb-3 ${isLeft ? "lg:flex-row-reverse" : ""}`}>
                            <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center" style={{ background: C.gold }}>
                              <Icon size={18} style={{ color: C.bg }} />
                            </div>
                            <span className="text-[10px] tracking-[0.35em] uppercase font-bold" style={{ fontFamily: F.sans, color: C.gold }}>STEP {i + 1}</span>
                          </div>
                          <h3 className="text-xl sm:text-2xl font-black tracking-tight mb-2" style={{ fontFamily: F.cond, color: C.text }}>{step.title}</h3>
                          <p className="text-sm leading-relaxed" style={{ fontFamily: F.sans, color: C.text2 }}>{step.desc}</p>
                        </div>
                        {/* Spacer for alternating layout */}
                        {isLeft && <div className="hidden lg:block" />}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Tools */}
        {tools.length > 0 && (
          <section ref={toolsRef} className="relative mc-grit" style={{ background: C.bg }} data-testid="mc-process-tools">
            <div className="max-w-7xl mx-auto px-6 py-14 md:py-20 relative z-10">
              <h2 className={`text-3xl sm:text-4xl font-black tracking-tight mb-10 text-center transition-all duration-700 ${show || toolsVis ? "opacity-100" : "opacity-0"}`}
                style={{ fontFamily: F.cond, color: C.text }}>THE TOOLS</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {tools.map((tool, i) => (
                  <div key={i} className={`p-6 text-center transition-all duration-600 ${show || toolsVis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                    style={{ transitionDelay: `${150 + i * 120}ms`, background: C.card, border: `1px solid ${C.bSub}` }}>
                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center" style={{ background: `${C.gold}10`, border: `1px solid ${C.bGold}` }}>
                      <Pen size={20} style={{ color: C.gold }} />
                    </div>
                    <h3 className="text-sm font-bold tracking-[0.15em] uppercase mb-2" style={{ fontFamily: F.sans, color: C.gold }}>{tool.name}</h3>
                    <p className="text-xs leading-relaxed" style={{ fontFamily: F.sans, color: C.text2 }}>{tool.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Quote + CTA */}
        <section className="relative mc-grit" style={{ background: C.bg2, borderTop: `1px solid ${C.bSub}` }} data-testid="mc-process-cta">
          <div className="max-w-7xl mx-auto px-6 py-14 relative z-10 text-center">
            <p className="text-2xl sm:text-3xl mb-2" style={{ fontFamily: F.hand, color: `${C.gold}60` }}>
              "No undo button. Just the marker and the moment."
            </p>
            <p className="text-xs mb-8" style={{ fontFamily: F.sans, color: C.text2 }}>— Mick</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/marker-collection/gallery" className="inline-flex items-center gap-3 px-7 py-3.5 text-xs tracking-[0.25em] uppercase font-bold transition-all hover:gap-5"
                style={{ fontFamily: F.sans, background: C.gold, color: C.bg }} data-testid="mc-process-gallery-cta">
                SEE THE RESULTS <ArrowRight size={14} />
              </Link>
              <Link to="/marker-collection/about" className="inline-flex items-center gap-3 px-7 py-3.5 text-xs tracking-[0.25em] uppercase font-bold border-2 transition-all hover:bg-white/5"
                style={{ fontFamily: F.sans, borderColor: C.text, color: C.text }} data-testid="mc-process-about-cta">
                MEET THE ARTIST
              </Link>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
